import React, { useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import { AG_GRID_LOCALE_BR } from '@ag-grid-community/locale';
import { AllCommunityModule, ModuleRegistry, themeQuartz } from 'ag-grid-community';
import type { ColDef, GridReadyEvent, GetRowIdParams } from 'ag-grid-community';
import type { CustomCellRendererProps } from 'ag-grid-react';
import {
  PlusIcon,
  ComputerDesktopIcon,
  ExclamationCircleIcon,
  EyeIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { useConferencias, type ConferenciasFilters } from '../hooks/useConferencias';
import ConferenciasStats from '../components/conferencias/ConferenciasStats';
import ConferenciasToolbar from '../components/conferencias/ConferenciasToolbar';
import type { ConferenciaListItem } from '../api/conferencia';
import { useConfirmDialog } from '../components/ui/ConfirmDialog';

ModuleRegistry.registerModules([AllCommunityModule]);

// Contexto do layout principal
interface OutletContextType {
  isFullscreen: boolean;
  setIsFullscreen: (value: boolean) => void;
}

// ── Badge de status ───────────────────────────────────────────────────────────

const statusConfig: Record<string, { label: string; classes: string; dot: string }> = {
  EM_ANDAMENTO: {
    label: 'Em Andamento',
    classes: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
    dot: 'bg-amber-500',
  },
  CONCLUIDO: {
    label: 'Concluído',
    classes: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
    dot: 'bg-emerald-500',
  },
  CANCELADO: {
    label: 'Cancelado',
    classes: 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300',
    dot: 'bg-rose-500',
  },
};

const StatusRenderer: React.FC<CustomCellRendererProps> = ({ value }) => {
  const cfg = statusConfig[value] ?? {
    label: value,
    classes: 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300',
    dot: 'bg-zinc-400',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${cfg.classes}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
};

// ── Data formatada ────────────────────────────────────────────────────────────

const DataRenderer: React.FC<CustomCellRendererProps> = ({ value }) => {
  if (!value) return <span className="text-zinc-300 dark:text-zinc-600">—</span>;
  try {
    const dt = new Date(value);
    return (
      <span>
        {dt.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
        {' '}
        <span className="text-zinc-400 dark:text-zinc-500">
          {dt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
        </span>
      </span>
    );
  } catch {
    return <span>{value}</span>;
  }
};

// ── Componente principal ──────────────────────────────────────────────────────

const ConferenciaListPage: React.FC = () => {
  const navigate = useNavigate();
  const confirm = useConfirmDialog();
  const gridRef = useRef<AgGridReact>(null);

  const outletContext = useOutletContext<OutletContextType>();
  const isFullscreen = outletContext?.isFullscreen ?? false;
  const setIsFullscreen = outletContext?.setIsFullscreen ?? (() => {});

  const [filters, setFilters] = useState<ConferenciasFilters>({ page: 1, pageSize: 12 });

  const {
    conferencias,
    stats,
    usuarios,
    isLoading,
    error,
    refetch,
    excluir,
    isExcluindo,
  } = useConferencias(filters);

  // ── Filtro client-side ──────────────────────────────────────────────────────

  const filteredData = useMemo(() => {
    let data = [...conferencias];

    if (filters.search) {
      const s = filters.search.toLowerCase();
      data = data.filter(
        c =>
          c.numeroNfe?.toLowerCase().includes(s) ||
          c.fornecedor?.toLowerCase().includes(s) ||
          c.codigoId?.toLowerCase().includes(s) ||
          c.usuario?.toLowerCase().includes(s),
      );
    }

    if (filters.status && filters.status !== 'TODOS') {
      data = data.filter(c => c.status === filters.status);
    }

    if (filters.dataInicio) {
      data = data.filter(c => c.dataConferencia >= filters.dataInicio!);
    }
    if (filters.dataFim) {
      data = data.filter(c => c.dataConferencia <= filters.dataFim! + 'T23:59:59');
    }

    if (filters.usuario) {
      data = data.filter(c => c.usuario === filters.usuario);
    }

    return data;
  }, [conferencias, filters]);

  // ── Actions ─────────────────────────────────────────────────────────────────

  const handleExcluir = useCallback(
    async (empresa: string, codigoId: string) => {
      const ok = await confirm({
        mensagem: `Excluir conferência ${codigoId}?`,
        destrutivo: true,
      });
      if (ok) excluir({ empresa, codigoId });
    },
    [confirm, excluir],
  );

  // ── Action renderer ─────────────────────────────────────────────────────────

  const ActionsRenderer: React.FC<CustomCellRendererProps> = useCallback(
    ({ data }: CustomCellRendererProps) => {
      if (!data) return null;
      const btnBase = 'inline-flex items-center justify-center w-7 h-7 rounded-md border-none cursor-pointer transition-colors';
      return (
        <div className="flex items-center gap-1 h-full">
          <button
            onClick={() => navigate(`/conferencia/${data.empresa}/${data.codigoId}`)}
            className={`${btnBase} bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400`}
            title="Visualizar"
            aria-label={`Visualizar conferência ${data.codigoId}`}
          >
            <EyeIcon className="w-4 h-4" />
          </button>
          <button
            onClick={() => navigate(`/conferencia/${data.empresa}/${data.codigoId}/editar`)}
            className={`${btnBase} bg-amber-50 text-amber-600 hover:bg-amber-100 dark:bg-amber-900/30 dark:text-amber-400`}
            title="Editar"
            aria-label={`Editar conferência ${data.codigoId}`}
          >
            <PencilSquareIcon className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleExcluir(data.empresa, data.codigoId)}
            disabled={isExcluindo}
            className={`${btnBase} bg-rose-50 text-rose-600 hover:bg-rose-100 dark:bg-rose-900/30 dark:text-rose-400 disabled:opacity-40`}
            title="Excluir"
            aria-label={`Excluir conferência ${data.codigoId}`}
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      );
    },
    [navigate, handleExcluir, isExcluindo],
  );

  // ── Columns ─────────────────────────────────────────────────────────────────

  const columnDefs = useMemo(
    (): ColDef<ConferenciaListItem>[] => [
      {
        field: 'numeroNfe',
        headerName: 'Nº NFe',
        width: 130,
        filter: 'agTextColumnFilter',
        pinned: 'left',
      },
      {
        field: 'fornecedor',
        headerName: 'Fornecedor',
        flex: 2,
        minWidth: 200,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 150,
        filter: 'agTextColumnFilter',
        cellRenderer: StatusRenderer,
        cellStyle: { display: 'flex', alignItems: 'center' },
      },
      {
        field: 'dataConferencia',
        headerName: 'Data',
        width: 165,
        filter: 'agDateColumnFilter',
        sort: 'desc',
        cellRenderer: DataRenderer,
        comparator: (a: string, b: string) => new Date(a).getTime() - new Date(b).getTime(),
      },
      {
        field: 'usuario',
        headerName: 'Usuário',
        width: 140,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'empresa',
        headerName: 'Empresa',
        width: 100,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'codigoId',
        headerName: 'Cód. ID',
        width: 100,
        filter: 'agTextColumnFilter',
      },
      {
        headerName: '',
        width: 110,
        sortable: false,
        filter: false,
        resizable: false,
        cellRenderer: ActionsRenderer,
        cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 },
      },
    ],
    [ActionsRenderer],
  );

  const getRowId = useCallback(
    (params: GetRowIdParams<ConferenciaListItem>) =>
      `${params.data.empresa}_${params.data.codigoId}`,
    [],
  );

  const onGridReady = useCallback((e: GridReadyEvent) => {
    e.api.sizeColumnsToFit();
  }, []);

  // ── Error state ─────────────────────────────────────────────────────────────

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-50 via-blue-50/30 to-indigo-50/20 flex items-center justify-center dark:from-zinc-900 dark:via-zinc-900 dark:to-zinc-800">
        <div className="bg-white rounded-xl border border-zinc-200 p-8 shadow-sm text-center max-w-md mx-4 dark:bg-zinc-800 dark:border-zinc-700">
          <ExclamationCircleIcon className="h-16 w-16 text-rose-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-zinc-900 mb-2 dark:text-white">
            Erro ao carregar conferências
          </h2>
          <p className="text-zinc-600 mb-6 dark:text-zinc-400">
            {error.message || 'Não foi possível carregar os dados. Tente novamente.'}
          </p>
          <button
            onClick={() => refetch()}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-gradient-to-br from-zinc-50 via-blue-50/30 to-indigo-50/20 dark:from-zinc-900 dark:via-zinc-900 dark:to-zinc-800">
      {/* Header */}
      <div className="flex-none border-b border-zinc-200/60 bg-white/80 backdrop-blur-xl dark:border-zinc-700/60 dark:bg-zinc-800/80">
        <div className="px-4 py-4 md:px-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ComputerDesktopIcon className="h-7 w-7 text-blue-600 dark:text-blue-400 flex-shrink-0" />
              <div className="min-w-0">
                <h1 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
                  Conferência de NFe
                </h1>
                <p className="text-xs text-zinc-500 dark:text-zinc-400">
                  Gerencie as conferências de Notas Fiscais Eletrônicas
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setIsFullscreen(!isFullscreen)}
                className="p-2 bg-zinc-100 hover:bg-zinc-200 rounded-lg transition-colors text-zinc-600 dark:bg-zinc-700 dark:hover:bg-zinc-600 dark:text-zinc-400"
                title="Alternar tela cheia"
              >
                {isFullscreen ? (
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9V4.5M9 9H4.5M9 9L3.5 3.5m11 5.5V4.5M15 9h4.5M15 9l5.5-5.5M9 15v4.5M9 15H4.5M9 15l-5.5 5.5m11-5.5v4.5m0-4.5h4.5m0 0l-5.5 5.5" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
                  </svg>
                )}
              </button>

              <button
                onClick={() => navigate('/conferencia/nova')}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center gap-2 font-medium shadow-sm"
              >
                <PlusIcon className="h-4 w-4" />
                Nova Conferência
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col overflow-hidden p-4 md:p-6 gap-4">
        {/* Stats */}
        {!isFullscreen && !isLoading && (
          <div className="flex-none">
            <ConferenciasStats stats={stats} isLoading={isLoading} />
          </div>
        )}

        {/* Toolbar */}
        <div className="flex-none">
          <ConferenciasToolbar
            filters={filters}
            setFilters={setFilters}
            usuarios={usuarios}
            onRefresh={refetch}
            isLoading={isLoading}
          />
        </div>

        {/* AG Grid */}
        <div className="flex-1 bg-white dark:bg-zinc-800 rounded-xl border border-zinc-200 dark:border-zinc-700 shadow-sm overflow-hidden">
          <AgGridReact<ConferenciaListItem>
            ref={gridRef}
            theme={themeQuartz}
            localeText={AG_GRID_LOCALE_BR}
            rowData={filteredData}
            columnDefs={columnDefs}
            getRowId={getRowId}
            onGridReady={onGridReady}
            pagination
            paginationPageSize={25}
            paginationPageSizeSelector={[10, 25, 50, 100]}
            enableCellTextSelection
            suppressCellFocus
            animateRows
            loading={isLoading}
            overlayNoRowsTemplate='<span style="color:#94a3b8;font-size:14px;padding:24px">Nenhuma conferência encontrada</span>'
            overlayLoadingTemplate='<span style="color:#94a3b8;font-size:14px">Carregando conferências...</span>'
          />
        </div>
      </div>
    </div>
  );
};

export default ConferenciaListPage;